import { createApi } from '@reduxjs/toolkit/query/react';

import { AgentType } from '../types/agent-types';
import { DomainEventSchema, FinchEventCode } from '../types/events-types';
import { baseQueryWithErrorHandling } from './baseQuery';

/**
 * Comprehensive pagination response wrapper for domain events
 */
export interface PaginatedEventsResponse<T> {
  /** Total number of events matching the query */
  count: number;
  /** URL for the next page of results, null if no next page */
  next: string | null;
  /** URL for the previous page of results, null if no previous page */
  previous: string | null;
  /** Array of events for the current page */
  results: T[];
}

/**
 * Valid ordering fields for domain events
 */
export type EventOrderingField =
  | 'occurred_at'
  | '-occurred_at'
  | 'event_code'
  | '-event_code'
  | 'user__email'
  | '-user__email'
  | 'user__first_name'
  | '-user__first_name'
  | 'user__last_name'
  | '-user__last_name'
  | 'agent'
  | '-agent'
  | 'target_type__app_label'
  | '-target_type__app_label'
  | 'target_type__model'
  | '-target_type__model'
  | 'matter__name'
  | '-matter__name'
  | 'matter__firm__name'
  | '-matter__firm__name';

/**
 * Target model types in the system
 */
export type TargetModelType =
  | 'app.task'
  | 'app.matter'
  | 'app.deliverable'
  | 'app.deliverable_match'
  | 'app.staff_note'
  | 'app.artifact'
  | 'app.artifact_digest';

/**
 * Comprehensive filtering parameters for domain events API with strong typing
 */
export interface EventsFilterParams {
  // Pagination parameters
  /** Number of events per page (default: 50, max: 1000) */
  limit?: number;
  /** Number of events to skip for pagination */
  offset?: number;

  // Event type filtering - strongly typed
  /** Single event code to filter by */
  event_code?: FinchEventCode;
  /** Multiple event codes for OR filtering - strongly typed array */
  event_code_in?: FinchEventCode[];
  /** Multiple event codes to exclude from results - strongly typed array */
  event_code_not_in?: FinchEventCode[];

  // User and agent filtering - strongly typed
  /** Filter by specific user UUID */
  user?: string;
  /** Filter by multiple user UUIDs - strongly typed array */
  user_in?: string[];
  /** Filter by specific agent type - strongly typed enum */
  agent?: AgentType;
  /** Filter by multiple agent types - strongly typed array */
  agent_in?: AgentType[];

  // Target entity filtering - strongly typed
  /** Filter by target entity UUID */
  target_id?: string;
  /** Filter by multiple target entity UUIDs - strongly typed array */
  target_id_in?: string[];
  /** Filter by target model type - strongly typed union */
  target_type?: TargetModelType;

  // Correlation filtering for workflow tracking
  /** Filter by correlation UUID */
  correlation_id?: string;
  /** Filter by multiple correlation UUIDs - strongly typed array */
  correlation_id_in?: string[];
  /** Filter events that have/don't have correlation IDs */
  has_correlation_id?: boolean;

  // Date and time filtering - ISO date strings
  /** Filter events that occurred before this date (ISO format) */
  occurred_at_before?: string;
  /** Filter events that occurred after this date (ISO format) */
  occurred_at_after?: string;
  /** Filter events that occurred on this specific date (YYYY-MM-DD) */
  occurred_at_date?: string;

  // Convenience date filters
  /** Filter events that occurred today */
  today?: boolean;
  /** Filter events that occurred this week */
  this_week?: boolean;
  /** Filter events that occurred this month */
  this_month?: boolean;

  // Search and ordering - strongly typed
  /** Full-text search across multiple event fields */
  search?: string;
  /**
   * Ordering field(s) - strongly typed array of valid ordering fields
   * Examples: ['occurred_at'], ['-created_at'], ['-occurred_at', 'event_code']
   */
  ordering?: EventOrderingField[];
}

/**
 * Simplified parameters for basic event queries (backward compatibility)
 */
export interface BasicEventParams {
  /** Event code to filter by */
  eventCode?: FinchEventCode;
  /** Number of events to return */
  limit?: number;
  /** Target entity UUID to filter by */
  targetId?: string;
}

/**
 * Advanced event query builder with fluent interface for complex filtering
 */
export class EventQueryBuilder {
  private params: EventsFilterParams = {};

  /** Set pagination parameters */
  paginate(limit: number, offset = 0): EventQueryBuilder {
    this.params.limit = limit;
    this.params.offset = offset;
    return this;
  }

  /** Filter by single event code */
  eventCode(code: FinchEventCode): EventQueryBuilder {
    this.params.event_code = code;
    return this;
  }

  /** Filter by multiple event codes */
  eventCodes(codes: FinchEventCode[]): EventQueryBuilder {
    this.params.event_code_in = codes;
    return this;
  }

  /** Exclude multiple event codes from results */
  excludeEventCodes(codes: FinchEventCode[]): EventQueryBuilder {
    this.params.event_code_not_in = codes;
    return this;
  }

  /** Filter by user UUID */
  user(userId: string): EventQueryBuilder {
    this.params.user = userId;
    return this;
  }

  /** Filter by multiple user UUIDs */
  users(userIds: string[]): EventQueryBuilder {
    this.params.user_in = userIds;
    return this;
  }

  /** Filter by agent type */
  agent(agentType: AgentType): EventQueryBuilder {
    this.params.agent = agentType;
    return this;
  }

  /** Filter by multiple agent types */
  agents(agentTypes: AgentType[]): EventQueryBuilder {
    this.params.agent_in = agentTypes;
    return this;
  }

  /** Filter by date range */
  dateRange(after?: string, before?: string): EventQueryBuilder {
    if (after) this.params.occurred_at_after = after;
    if (before) this.params.occurred_at_before = before;
    return this;
  }

  /** Filter events from today */
  today(): EventQueryBuilder {
    this.params.today = true;
    return this;
  }

  /** Filter events from this week */
  thisWeek(): EventQueryBuilder {
    this.params.this_week = true;
    return this;
  }

  /** Filter events from this month */
  thisMonth(): EventQueryBuilder {
    this.params.this_month = true;
    return this;
  }

  /** Add search term */
  search(term: string): EventQueryBuilder {
    this.params.search = term;
    return this;
  }

  /** Set ordering */
  orderBy(...fields: EventOrderingField[]): EventQueryBuilder {
    this.params.ordering = fields;
    return this;
  }

  /** Filter by correlation ID */
  correlationId(id: string): EventQueryBuilder {
    this.params.correlation_id = id;
    return this;
  }

  /** Filter by target type */
  targetType(type: TargetModelType): EventQueryBuilder {
    this.params.target_type = type;
    return this;
  }

  /** Build the final parameters object */
  build(): EventsFilterParams {
    return { ...this.params };
  }
}

/**
 * Utility function to build query string from strongly typed filter parameters
 */
function buildEventQueryString(
  params: EventsFilterParams | BasicEventParams,
): string {
  const searchParams = new URLSearchParams();

  Object.entries(params).forEach(([key, value]) => {
    if (value !== undefined && value !== null) {
      // Handle array values by converting to comma-separated strings
      if (Array.isArray(value)) {
        if (value.length > 0) {
          const apiKey = key.replace(/([A-Z])/g, '_$1').toLowerCase();
          searchParams.append(apiKey, value.join(','));
        }
      } else {
        // Convert camelCase to snake_case for API compatibility
        const apiKey = key.replace(/([A-Z])/g, '_$1').toLowerCase();

        // Handle legacy eventCode and targetId mapping
        if (key === 'eventCode') {
          searchParams.append('event_code', value.toString());
        } else if (key === 'targetId') {
          searchParams.append('target_id', value.toString());
        } else {
          searchParams.append(apiKey, value.toString());
        }
      }
    }
  });

  return searchParams.toString();
}

/**
 * Helper functions for creating common event queries
 */
export const EventQueries = {
  /** Get recent task events for a specific user */
  recentTaskEvents: (userId: string, limit = 50): EventsFilterParams =>
    new EventQueryBuilder()
      .eventCodes([
        FinchEventCode.TASK_CREATED,
        FinchEventCode.TASK_STATUS_CHANGED,
        FinchEventCode.TASK_ASSIGNED,
      ])
      .user(userId)
      .thisWeek()
      .orderBy('-occurred_at')
      .paginate(limit)
      .build(),

  /** Get system events with correlation tracking */
  systemEventsWithCorrelation: (
    afterDate: string,
    limit = 100,
  ): EventsFilterParams =>
    new EventQueryBuilder()
      .agent(AgentType.SYSTEM)
      .dateRange(afterDate)
      .orderBy('-occurred_at')
      .paginate(limit)
      .build(),

  /** Get workflow events by correlation ID */
  workflowEvents: (correlationId: string): EventsFilterParams =>
    new EventQueryBuilder()
      .correlationId(correlationId)
      .orderBy('occurred_at') // Chronological order
      .build(),

  /** Search for error events */
  errorEvents: (searchTerm = 'error'): EventsFilterParams =>
    new EventQueryBuilder().search(searchTerm).orderBy('-occurred_at').build(),

  /** Get all visible events (excluding silenced events) */
  visibleEvents: (limit = 50): EventsFilterParams =>
    new EventQueryBuilder()
      .excludeEventCodes([
        FinchEventCode.BLOB_TRANSFORM_COMPLETED,
        FinchEventCode.DELIVERABLE_CREATED,
        FinchEventCode.DELIVERABLE_STATUS_CHANGED,
        FinchEventCode.DELIVERABLE_SATISFIED,
        FinchEventCode.DELIVERABLE_MATCH_STATUS_CHANGED,
        FinchEventCode.DELIVERABLE_MATCH_APPROVED,
        FinchEventCode.ARTIFACT_CREATED,
        FinchEventCode.ARTIFACT_DIGEST_CREATED,
        FinchEventCode.ARTIFACT_DIGEST_STATUS_UPDATED,
      ])
      .orderBy('-occurred_at')
      .paginate(limit)
      .build(),
} as const;

/**
 * Redux Toolkit Query API for Domain Events with strong typing
 */
export const eventsApi = createApi({
  reducerPath: 'eventsApi',
  tagTypes: ['Events', 'Event'],
  baseQuery: baseQueryWithErrorHandling,
  endpoints: (builder) => ({
    /**
     * Get events for a specific matter with comprehensive filtering
     *
     * @param matterId - UUID of the matter to get events for
     * @param filters - Strongly typed filtering, pagination, search, and ordering options
     * @returns Paginated response containing events for the specified matter
     *
     * @example
     * ```typescript
     * // Get recent task events for a matter
     * const { data } = useGetMatterEventsQuery({
     *   matterId: 'matter-uuid',
     *   event_code_in: [FinchEventCode.TASK_CREATED, FinchEventCode.TASK_UPDATED],
     *   this_week: true,
     *   ordering: ['-occurred_at'],
     *   limit: 100
     * });
     *
     * // Using the query builder
     * const { data } = useGetMatterEventsQuery({
     *   matterId: 'matter-uuid',
     *   ...EventQueries.recentTaskEvents('user-uuid')
     * });
     * ```
     */
    getMatterEvents: builder.query<
      PaginatedEventsResponse<DomainEventSchema<FinchEventCode>>,
      { matterId: string } & EventsFilterParams
    >({
      query: ({ matterId, ...filters }) => {
        const queryString = buildEventQueryString(filters);
        return `api/matters/${matterId}/events/${queryString ? `?${queryString}` : ''}`;
      },
      providesTags: (result, error, { matterId }) => [
        { type: 'Events', id: matterId },
        ...(result?.results || []).map(({ id }) => ({
          type: 'Event' as const,
          id,
        })),
      ],
    }),

    /**
     * Get events for a specific matter (legacy interface for backward compatibility)
     *
     * @deprecated Use getMatterEvents with EventsFilterParams for full functionality
     */
    getMatterEventsLegacy: builder.query<
      DomainEventSchema<FinchEventCode>[],
      { matterId: string } & BasicEventParams
    >({
      query: ({ matterId, ...params }) => {
        const queryString = buildEventQueryString(params);
        return `api/matters/${matterId}/events/${queryString ? `?${queryString}` : ''}`;
      },
      transformResponse: (
        response: PaginatedEventsResponse<DomainEventSchema<FinchEventCode>>,
      ) => response.results,
      providesTags: (result, error, { matterId }) => [
        { type: 'Events', id: matterId },
        ...(result || []).map(({ id }) => ({ type: 'Event' as const, id })),
      ],
    }),

    /**
     * Get a specific event by ID within a matter context
     *
     * @param matterId - UUID of the matter
     * @param eventId - UUID of the specific event
     * @returns Complete event details including full payload and user information
     */
    getMatterEvent: builder.query<
      DomainEventSchema<FinchEventCode>,
      { matterId: string; eventId: string }
    >({
      query: ({ matterId, eventId }) =>
        `api/matters/${matterId}/events/${eventId}/`,
      providesTags: (result, error, { eventId }) => [
        { type: 'Event', id: eventId },
      ],
    }),

    /**
     * Get all events across all matters with comprehensive filtering (staff only)
     *
     * @param filters - Strongly typed filtering, pagination, search, and ordering options
     * @returns Paginated response containing events from all matters in the system
     *
     * @example
     * ```typescript
     * // Get recent system events with correlation tracking
     * const { data } = useGetAllEventsQuery({
     *   agent: AgentType.SYSTEM,
     *   has_correlation_id: true,
     *   occurred_at_after: '2024-01-01T00:00:00Z',
     *   ordering: ['-occurred_at'],
     *   limit: 100
     * });
     *
     * // Using predefined query
     * const { data } = useGetAllEventsQuery(
     *   EventQueries.systemEventsWithCorrelation('2024-01-01T00:00:00Z')
     * );
     * ```
     */
    getAllEvents: builder.query<
      PaginatedEventsResponse<DomainEventSchema<FinchEventCode>>,
      EventsFilterParams | void
    >({
      query: (filters = {}) => {
        const queryString = buildEventQueryString(filters || {});
        return `api/events/${queryString ? `?${queryString}` : ''}`;
      },
      providesTags: (result) => [
        { type: 'Events', id: 'ALL' },
        ...(result?.results || []).map(({ id }) => ({
          type: 'Event' as const,
          id,
        })),
      ],
    }),

    /**
     * Get all events across all matters (legacy interface for backward compatibility)
     *
     * @deprecated Use getAllEvents with EventsFilterParams for full functionality
     */
    getAllEventsLegacy: builder.query<
      DomainEventSchema<FinchEventCode>[],
      BasicEventParams | void
    >({
      query: (params = {}) => {
        const queryString = buildEventQueryString(params || {});
        return `api/events/${queryString ? `?${queryString}` : ''}`;
      },
      transformResponse: (
        response: PaginatedEventsResponse<DomainEventSchema<FinchEventCode>>,
      ) => response.results,
      providesTags: (result) => [
        { type: 'Events', id: 'ALL' },
        ...(result || []).map(({ id }) => ({ type: 'Event' as const, id })),
      ],
    }),

    /**
     * Get a specific event by ID from the global events endpoint
     *
     * @param eventId - UUID of the specific event
     * @returns Complete event details including full payload and user information
     */
    getEvent: builder.query<DomainEventSchema<FinchEventCode>, string>({
      query: (eventId) => `api/events/${eventId}/`,
      providesTags: (result, error, eventId) => [
        { type: 'Event', id: eventId },
      ],
    }),

    /**
     * Get events by correlation ID for workflow tracking
     *
     * @param correlationId - UUID of the correlation ID to track
     * @param additionalFilters - Additional strongly typed filtering options
     * @returns All events associated with the specified correlation ID
     *
     * @example
     * ```typescript
     * // Track all events in a workflow
     * const { data } = useGetEventsByCorrelationQuery({
     *   correlationId: 'workflow-uuid',
     *   ordering: ['occurred_at'] // Chronological order
     * });
     * ```
     */
    getEventsByCorrelation: builder.query<
      PaginatedEventsResponse<DomainEventSchema<FinchEventCode>>,
      { correlationId: string } & Omit<EventsFilterParams, 'correlation_id'>
    >({
      query: ({ correlationId, ...filters }) => {
        const queryString = buildEventQueryString({
          ...filters,
          correlation_id: correlationId,
        });
        return `api/events/${queryString ? `?${queryString}` : ''}`;
      },
      providesTags: (result, error, { correlationId }) => [
        { type: 'Events', id: `correlation-${correlationId}` },
        ...(result?.results || []).map(({ id }) => ({
          type: 'Event' as const,
          id,
        })),
      ],
    }),
  }),
});

// Export hooks for use in components
export const {
  useGetMatterEventsQuery,
  useGetMatterEventsLegacyQuery,
  useGetMatterEventQuery,
  useGetAllEventsQuery,
  useGetAllEventsLegacyQuery,
  useGetEventQuery,
  useGetEventsByCorrelationQuery,
} = eventsApi;

// Export hook aliases for backward compatibility
export const useGetEventsQuery = useGetMatterEventsLegacyQuery;
export const useGetGlobalEventsQuery = useGetAllEventsLegacyQuery;
